import React, { useState, useEffect, useCallback } from 'react';
import { CalendarOff, CheckCircle, XCircle, Send, Edit, RefreshCw, Filter } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';

const STATUS_LABELS = {
  PENDING: { label: 'Nowe', className: 'bg-yellow-100 text-yellow-800' },
  APPROVED: { label: 'Zaakceptowane', className: 'bg-blue-100 text-blue-800' },
  REJECTED: { label: 'Odrzucone', className: 'bg-red-100 text-red-800' },
  SENT_TO_NFZ: { label: 'Wysłane do NFZ', className: 'bg-green-100 text-green-800' },
};

const formatDate = (s) => (s ? new Date(s).toLocaleDateString('pl-PL') : '');
const formatDateTime = (s) => (s ? new Date(s).toLocaleString('pl-PL') : '');

const DoctorAbsencesList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', pwz: '', dateFrom: '', dateTo: '' });
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [actionModal, setActionModal] = useState(null); // { id, type: 'approve'|'reject'|'edit', initialComment }
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, size: 50 };
      if (filters.status) params.status = filters.status;
      if (filters.pwz) params.pwz = filters.pwz;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      const response = await api.get('/doctor-absences', { params });
      setItems(response.data.items || []);
      setTotalPages(response.data.totalPages || 0);
      setTotalElements(response.data.totalElements || 0);
    } catch (error) {
      const msg = error?.response?.data?.error;
      toast.error(msg || 'Błąd podczas pobierania zgłoszeń');
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applyFilters = (e) => {
    e.preventDefault();
    setPage(0);
    fetchData();
  };

  const resetFilters = () => {
    setFilters({ status: '', pwz: '', dateFrom: '', dateTo: '' });
    setPage(0);
  };

  const openAction = (id, type, initialComment = '') => {
    setActionModal({ id, type });
    setComment(initialComment || '');
  };

  const closeAction = () => {
    setActionModal(null);
    setComment('');
  };

  const submitAction = async () => {
    if (!actionModal) return;
    setSubmitting(true);
    try {
      const { id, type } = actionModal;
      if (type === 'approve') {
        await api.post(`/doctor-absences/${id}/approve`, { comment });
        toast.success('Zgłoszenie zaakceptowane');
      } else if (type === 'reject') {
        if (!comment.trim()) {
          toast.error('Podaj powód odrzucenia');
          setSubmitting(false);
          return;
        }
        await api.post(`/doctor-absences/${id}/reject`, { comment });
        toast.success('Zgłoszenie odrzucone');
      } else if (type === 'edit') {
        await api.put(`/doctor-absences/${id}`, { hrComment: comment });
        toast.success('Komentarz zapisany');
      }
      closeAction();
      fetchData();
    } catch (error) {
      const msg = error?.response?.data?.error;
      toast.error(msg || 'Operacja nie powiodła się');
    } finally {
      setSubmitting(false);
    }
  };

  const markSentToNfz = async (id) => {
    if (!window.confirm('Oznaczyć zgłoszenie jako wysłane do NFZ?')) return;
    try {
      await api.post(`/doctor-absences/${id}/mark-sent-nfz`);
      toast.success('Oznaczono jako wysłane do NFZ');
      fetchData();
    } catch (error) {
      const msg = error?.response?.data?.error;
      toast.error(msg || 'Operacja nie powiodła się');
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <CalendarOff className="text-indigo-600" size={28} />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Nieobecności lekarzy</h1>
            <p className="text-sm text-gray-500">Zgłoszenia urlopów / nieobecności otrzymane przez publiczny formularz</p>
          </div>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <RefreshCw size={16} /> Odśwież
        </button>
      </div>

      <form onSubmit={applyFilters} className="bg-white rounded-md shadow p-4 mb-4">
        <div className="flex items-center gap-2 mb-3 text-gray-700">
          <Filter size={16} />
          <span className="text-sm font-medium">Filtry</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
            >
              <option value="">Wszystkie</option>
              <option value="PENDING">Nowe</option>
              <option value="APPROVED">Zaakceptowane</option>
              <option value="REJECTED">Odrzucone</option>
              <option value="SENT_TO_NFZ">Wysłane do NFZ</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Numer PWZ</label>
            <input
              type="text"
              value={filters.pwz}
              onChange={(e) => setFilters({ ...filters, pwz: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
              placeholder="np. 1234567"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Okres od</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Okres do</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              type="submit"
              className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm"
            >
              Zastosuj
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 rounded-md text-sm"
            >
              Wyczyść
            </button>
          </div>
        </div>
      </form>

      <div className="bg-white rounded-md shadow overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center"><LoadingSpinner /></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-gray-500">Brak zgłoszeń spełniających kryteria.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-left">Zgłoszono</th>
                  <th className="px-4 py-3 text-left">PWZ / Lekarz</th>
                  <th className="px-4 py-3 text-left">Okres</th>
                  <th className="px-4 py-3 text-left">Uwagi</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Komentarz kadr</th>
                  <th className="px-4 py-3 text-right">Akcje</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {items.map((it) => {
                  const status = STATUS_LABELS[it.status] || { label: it.status, className: 'bg-gray-100 text-gray-700' };
                  return (
                    <tr key={it.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 align-top whitespace-nowrap text-gray-700">
                        {formatDateTime(it.createdAt)}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-gray-900">{it.pwzNumber}</div>
                        {(it.doctorFirstName || it.doctorLastName) && (
                          <div className="text-xs text-gray-500">
                            {[it.doctorFirstName, it.doctorLastName].filter(Boolean).join(' ')}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        {formatDate(it.startDate)} – {formatDate(it.endDate)}
                      </td>
                      <td className="px-4 py-3 align-top max-w-xs">
                        <div className="text-gray-700 whitespace-pre-wrap break-words">{it.notes || '—'}</div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${status.className}`}>
                          {status.label}
                        </span>
                        {it.sentToNfzAt && (
                          <div className="text-xs text-gray-500 mt-1">{formatDateTime(it.sentToNfzAt)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top max-w-xs">
                        <div className="text-gray-700 whitespace-pre-wrap break-words">{it.hrComment || '—'}</div>
                        {it.processedByName && (
                          <div className="text-xs text-gray-500 mt-1">
                            {it.processedByName}{it.processedAt ? `, ${formatDateTime(it.processedAt)}` : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          {it.status === 'PENDING' && (
                            <>
                              <button
                                title="Zaakceptuj"
                                onClick={() => openAction(it.id, 'approve', it.hrComment)}
                                className="p-1.5 rounded hover:bg-green-100 text-green-700"
                              >
                                <CheckCircle size={18} />
                              </button>
                              <button
                                title="Odrzuć"
                                onClick={() => openAction(it.id, 'reject', it.hrComment)}
                                className="p-1.5 rounded hover:bg-red-100 text-red-700"
                              >
                                <XCircle size={18} />
                              </button>
                            </>
                          )}
                          {it.status === 'APPROVED' && (
                            <button
                              title="Oznacz jako wysłane do NFZ"
                              onClick={() => markSentToNfz(it.id)}
                              className="p-1.5 rounded hover:bg-blue-100 text-blue-700"
                            >
                              <Send size={18} />
                            </button>
                          )}
                          {it.status !== 'SENT_TO_NFZ' && (
                            <button
                              title="Edytuj komentarz"
                              onClick={() => openAction(it.id, 'edit', it.hrComment)}
                              className="p-1.5 rounded hover:bg-gray-100 text-gray-700"
                            >
                              <Edit size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t">
            <span className="text-sm text-gray-600">
              Strona {page + 1} z {totalPages} ({totalElements} zgłoszeń)
            </span>
            <div className="flex gap-2">
              <button
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
                className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
              >
                Poprzednia
              </button>
              <button
                disabled={page + 1 >= totalPages}
                onClick={() => setPage(page + 1)}
                className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
              >
                Następna
              </button>
            </div>
          </div>
        )}
      </div>

      {actionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-5 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {actionModal.type === 'approve' && 'Zaakceptuj zgłoszenie'}
                {actionModal.type === 'reject' && 'Odrzuć zgłoszenie'}
                {actionModal.type === 'edit' && 'Edytuj komentarz kadr'}
              </h2>
            </div>
            <div className="p-5">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Komentarz {actionModal.type === 'reject' && <span className="text-red-500">*</span>}
              </label>
              <textarea
                rows={4}
                maxLength={2000}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder={actionModal.type === 'reject' ? 'Podaj powód odrzucenia' : 'Opcjonalny komentarz'}
              />
            </div>
            <div className="p-5 border-t bg-gray-50 flex justify-end gap-2">
              <button
                disabled={submitting}
                onClick={closeAction}
                className="px-4 py-2 text-sm bg-white border border-gray-300 rounded hover:bg-gray-100"
              >
                Anuluj
              </button>
              <button
                disabled={submitting}
                onClick={submitAction}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded disabled:bg-blue-300"
              >
                {submitting ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorAbsencesList;
